// src/components/common/AuthErrorBoundary.js
import React from 'react';
import { Box, Button, Typography, Paper, Alert } from '@mui/material';
import { LockOpen, Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { handleLogin } from '../../services/authService';

/**
 * Specialized error boundary for authentication-related errors
 * Provides specific actions for auth failures like expired tokens, etc.
 */
class AuthErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isAuthError: false
    };
  }

  static getDerivedStateFromError(error) {
    // Check if it's an auth-related error
    const isAuthError = 
      error?.name === 'InteractionRequiredAuthError' ||
      error?.message?.includes('token') ||
      error?.message?.includes('authentication') ||
      error?.message?.includes('unauthorized') ||
      error?.response?.status === 401;

    return {
      hasError: true,
      isAuthError
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('AuthErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({ error });

    // If it's an auth error, clear stale auth data
    if (this.state.isAuthError) {
      console.log('Authentication error detected, clearing stale data');
      // Could trigger logout or token refresh here
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      isAuthError: false
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <AuthErrorFallback
          error={this.state.error}
          isAuthError={this.state.isAuthError}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

const AuthErrorFallback = ({ error, isAuthError, onReset }) => {
  const { t } = useTranslation('common');
  const { instance } = useMsal();

  const handleLoginClick = async () => {
    try {
      await handleLogin(instance);
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  if (isAuthError) {
    return (
      <Box
        sx={{
          minHeight: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3
        }}
      >
        <Paper
          elevation={2}
          sx={{
            p: 4,
            maxWidth: 500,
            textAlign: 'center'
          }}
        >
          <LockOpen sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          
          <Typography variant="h5" gutterBottom>
            {t('authError.title', 'Authentication Required')}
          </Typography>

          <Alert severity="warning" sx={{ mb: 3 }}>
            {t('authError.sessionExpired', 'Your session has expired or authentication is required.')}
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('authError.message', 'Please login again to continue using the application.')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<LockOpen />}
              onClick={handleLoginClick}
            >
              {t('authError.login', 'Login')}
            </Button>

            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={onReset}
            >
              {t('authError.tryAgain', 'Try Again')}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Not an auth error, show generic error
  return (
    <Box
      sx={{
        minHeight: '400px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3
      }}
    >
      <Paper elevation={2} sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom color="error">
          {t('error.generic', 'An error occurred')}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {error?.message || t('error.unknown', 'An unexpected error occurred')}
        </Typography>

        <Button variant="outlined" onClick={onReset}>
          {t('error.tryAgain', 'Try Again')}
        </Button>
      </Paper>
    </Box>
  );
};

export default AuthErrorBoundary;
